/* eslint-disable */
import { Component, ElementRef, HostBinding, HostListener, OnInit, Renderer2 } from '@angular/core';
import { LoadingController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { OverlayBaseController } from '@ionic/angular/util/overlay';
import { TranslateService } from '@ngx-translate/core';
import { AppAdapter } from 'src/app/adapters/app-info/app.adapter';
import { DeviceAdapter } from 'src/app/adapters/device/device.adapter';
import { MSRemoteLoggerCategory, MSRemoteLoggerData } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { ApiServiceOld } from 'src/app/services/api-old.service';
import { AuthService } from 'src/app/services/auth.service';
import { CONSTANTS } from 'src/app/services/config.service';
import { LoggerService } from 'src/app/services/logger.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { PosterService } from 'src/app/services/poster.service';
import { PrefsService } from 'src/app/services/prefs.service';
import { RegistrationService } from 'src/app/services/registration/registration.service';
import { isTopOverlay, restoreFocusOnDismissModal } from 'src/app/utils/overlay-controller.utils';
import { PlatformEnum } from 'src/app/utils/platform.utils';
import { environment } from 'src/environments/environment';
import { ContactPage } from '../contact/contact.page';

export enum SignupDetailSteps {
  ENTER_EMAIL = 'enterEmail',
  PARTICIPATION_CODE = 'participationCode',
  RETRIEVE_EMAIL = 'retrieveEmail',
  ENTER_RETRIEVED_CODE = 'enterRetrievedCode'
}

@Component({
  selector: 'app-signup-detail',
  templateUrl: './signup-detail.page.html',
  styleUrls: ['./signup-detail.page.scss']
})
export class SignupDetailPage implements OnInit {
  SignupDetailSteps = SignupDetailSteps;

  @HostBinding('attr.aria-hidden') ariaHidden = false;

  PERSON_NOT_FOUND_ERROR_MESSAGE = 'Person not found';

  currentStep: SignupDetailSteps = SignupDetailSteps.ENTER_EMAIL;
  firstStep: SignupDetailSteps = SignupDetailSteps.ENTER_EMAIL;

  // metadata for authorizing on device
  appVer: string = environment.browserAppConfig.version;
  appId: string = environment.browserAppConfig.appName;
  device: PlatformEnum;

  // data passed from signup page
  inviteCode: string = null;
  alreadyActivated: boolean;
  isModal: boolean;
  pageWidth: number;

  // data entered on page
  email: string;
  participationCode: string;

  // data from prefs
  signupEmail: string;

  // page
  translationData: any;
  submitDisabled = false;

  // errors
  connectionFailed = false;
  codeIsBad = false;
  unknownIssue = false;
  invalidEmail = false;
  processingCodeError = false;

  constructor(
    private platform: Platform,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private translate: TranslateService,
    private logger: LoggerService,
    private poster: PosterService,
    private api: ApiServiceOld,
    private registration: RegistrationService,
    private nav: NavigationService,
    private prefs: PrefsService,
    private auth: AuthService,
    private deviceAdapter: DeviceAdapter,
    private appAdapter: AppAdapter,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  /***********************************************************************************************************************************************************************
   * Helpers
   ***********************************************************************************************************************************************************************/

  isValidEmail() {
    const re = /^\S+@\S+\.\S+$/; // using a short re to make sure basic parts are present; not bullet-proof (see http://stackoverflow.com/questions/46155/validate-email-address-in-javascript)
    return re.test(this.email);
  }

  resetValidation() {
    this.connectionFailed = false;
    this.invalidEmail = false;
    this.unknownIssue = false;
    this.processingCodeError = false;
  }

  onInputFocus() {
    if (CONSTANTS.IS_DEVICE === false) {
      return;
    }
  }

  onInputBlur() {
    if (CONSTANTS.IS_DEVICE === false) {
      return;
    }
  }

  authenticate(authorizeData) {
    const registrationData = Object.assign(authorizeData, { authCode: this.inviteCode, appVersion: this.appVer, appId: this.appId });
    return this.registration.authenticate(registrationData);
  }

  async handleAuthorizedCode(authorizeData, hasValidParticipationCode, isAlreadyActivated, logMsg, loading) {
    // let nextPage;
    const authCode: string = authorizeData.household_authcode;
    let targetPage;

    this.authenticate(authorizeData)
      .then(() => this.poster.initialize())
      .then(() => this.auth.updateLog(logMsg))
      .then(() => {
        if (hasValidParticipationCode === true) {
          this.prefs.getStorePromise('completedSignup', true).then(() => {
            this.registration.goToNextPage().then((target) => {
              targetPage = target;

              this.logger.info('Device details - signup-details', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, {
                width: this.platform.width(),
                height: this.platform.height(),
                platforms: this.platform.platforms()
              });

              this.nav.nav(targetPage);
              this.dismiss();
              loading.dismiss();
              this.submitDisabled = false;
            });
          });
        } else {
          this.alreadyActivated = isAlreadyActivated;
          loading.dismiss();
          this.submitDisabled = false;
          this.ionViewWillEnter();
        }
      })
      .catch((err) => {
        loading.dismiss();
        this.submitDisabled = false;
        this.logger.log('Error authenticating from signup', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, err);
      });
  }

  /***********************************************************************************************************************************************************************
   * Used or Unused invite code -> prompt for participation code
   ***********************************************************************************************************************************************************************/

  async onSubmitParticipationCode() {
    let logMsg = '';
    if (this.submitDisabled === true) {
      return;
    }
    this.resetValidation();
    this.submitDisabled = true;

    if (this.participationCode) {
      this.participationCode = this.participationCode.trim();
    }

    // when calling authorize, we use participation code as authCode
    const postData = { authCode: this.participationCode, appVersion: this.appVer, appId: this.appId, device: this.device };

    const loading = await this.loadingCtrl.create({});
    await loading.present();

    this.api.post(CONSTANTS.authorizePath, postData).subscribe(
      (res) => {
        this.logger.log('Resolved authorize', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, res as MSRemoteLoggerData);

        let hasValidParticipationCode: boolean;
        let isAlreadyActivated: boolean;
        let abort = false;

        if (res.hasOwnProperty('fully_authorized') && res['fully_authorized'] === 1) {
          hasValidParticipationCode = true;
          isAlreadyActivated = false;
          logMsg = 'Signup detail: Success - entered valid participation code. Code: ' + this.inviteCode;
        } else if (res.hasOwnProperty('abortMessageKey') && res['abortMessageKey'] === 'ABORT_MESSAGES.ALREADY_ACTIVATED') {
          hasValidParticipationCode = false;
          isAlreadyActivated = true;
          logMsg = 'Signup detail: Error - entered used invitation code instead of participation code. Code: ' + this.inviteCode;
          res['abortMessageKey'] = null; // don't save this abort message key in prefs or could end up on unavailable page
        } else if (res.hasOwnProperty('abortMessageKey')) {
          abort = true;
          logMsg = 'Signup: Error - aborted. Key: ' + res['abortMessageKey'] + ' Code: ' + this.inviteCode;
        } else {
          hasValidParticipationCode = false;
          isAlreadyActivated = false;
          logMsg = 'Signup: Success - entered new invitation code. Code: ' + this.inviteCode;
        }

        if (hasValidParticipationCode === true) {
          this.handleAuthorizedCode(res, hasValidParticipationCode, isAlreadyActivated, logMsg, loading);
          // loading.dismiss();
        } else {
          // for now, just throw a 'bad code' error if they enter an invitation code
          // we could have special workflow for this case, but not bothering yet
          this.auth.updateLog(logMsg).then(() => {
            this.codeIsBad = true;
            this.processingCodeError = true;
            this.submitDisabled = false;
            loading.dismiss();
          });
        }
      },
      (err) => {
        this.logger.log('Error posting to authorize', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, err.status, err.data, err.error);

        if (err.status === 0) {
          this.connectionFailed = true;
          logMsg = 'Signup detail: Error - connection failed.';
        } else if (err.status === 409) {
          this.codeIsBad = true;
          logMsg = 'Signup detail: Error - bad code. Code: ' + this.participationCode;
        } else {
          this.unknownIssue = true;
          logMsg = 'Signup detail: Error - unknown issue.';
        }

        this.auth.updateLog(logMsg).then(() => {
          this.processingCodeError = true;
          this.submitDisabled = false;
          loading.dismiss();
        });
      }
    );
  }

  /***********************************************************************************************************************************************************************
   * Unused invite code or code retrieval -> prompt for email
   ***********************************************************************************************************************************************************************/

  // after entering an un-used invite code on signup, user submits email
  // or user can retrieve participation code by providing email. they may or may not include an access code.
  async onSubmitEmail(isRetrievingCode) {
    let logMsg = '';
    if (this.submitDisabled === true) {
      return;
    }
    this.resetValidation();

    if (!this.isValidEmail()) {
      this.invalidEmail = true;
      this.processingCodeError = true;
      return;
    }

    this.submitDisabled = true; // prevent double click/submission
    this.translationData.email = null;
    setTimeout(() => {
      this.translationData.email = this.email; // update translationData. Doesn't happen automatically and need this for text on participation code screen.
    });

    // we have invite code if they entered it on signup page
    // we don't have it if they went straight to code retrieval without entering a code on signup
    const params = {
      email: this.email,
      locale: this.translate.getBrowserCultureLang(),
      authCode: this.inviteCode,
      prevEmail: this.signupEmail
    };
    if (!this.inviteCode) {
      delete params.authCode;
    } // don't include authCode if it's null/undefined

    const loading = await this.loadingCtrl.create({});
    await loading.present();

    this.api.post(CONSTANTS.emailPasscodePath, params).subscribe(
      (res) => {
        // always returns error
        alert('success');
      },
      (err) => {
        // if email address was successfully received, send to participation code entry page
        // if email didn't match any in our system, we still send them forward
        if (err.status === 200 || (err.status === 404 && err.error === this.PERSON_NOT_FOUND_ERROR_MESSAGE)) {
          // if they entered an unrecognized email, we still proceed
          if (err.status === 404) {
            logMsg = 'Signup detail: Warn - enetered unknown email for code retrieval';
          } else if (isRetrievingCode) {
            logMsg = 'Signup detail: Success - entered email for code retrieval';
          } else {
            logMsg = 'Signup detail: Success - entered email for first login';
          }

          this.auth
            .updateLog(logMsg)
            .then(() => (err.status === 200 && this.inviteCode ? this.auth.setSignupEmail(this.email) : Promise.resolve()))
            .then(async () => {
              // if the user submitted a
              if (err.status === 200) {
                this.signupEmail = this.email;
              }

              this.currentStep = isRetrievingCode ? SignupDetailSteps.ENTER_RETRIEVED_CODE : SignupDetailSteps.PARTICIPATION_CODE;
              this.submitDisabled = false;
              await loading.dismiss();
            });

        } else {
          // Other 404 conditions are 1) Email template N/A and 2) No call center email
          // Throw an alert in these cases since they're required for the study
          this.logger.log('Error sending code to email address', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, err.status, err.data, err.error);

          if (err.status === 0) {
            this.connectionFailed = true;
            logMsg = 'Signup detail: Error - connection failed. Code: ' + this.email;
          } else if (err.status === 404) {
            alert('Error: ' + err.message);
          } else {
            this.unknownIssue = true;
            logMsg = 'Signup detail: Error - unknown issue. Code: ' + this.email;
          }

          this.auth.updateLog(logMsg).then(() => {
            this.processingCodeError = true;
            this.submitDisabled = false;
            loading.dismiss();
          });
        }
      }
    );
  }

  /***********************************************************************************************************************************************************************
   * Launch contact page
   ***********************************************************************************************************************************************************************/

  async presentModalContact() {
    const data = {

      inviteCode: this.inviteCode,
      email: this.email,
      isModal: true
    };

    let popup = await this.modalCtrl.create({
      component: ContactPage,
      componentProps: data,
      backdropDismiss: true
    });
    this.ariaHidden = true;

    restoreFocusOnDismissModal(popup);
    popup.onDidDismiss().then(() => {
      this.ariaHidden = false;
    });
    await popup.present();
  }

  /***********************************************************************************************************************************************************************
   * Footer button options
   ***********************************************************************************************************************************************************************/

  // user clicks that they need to retrieve code. navigate to email entry page for retrieval
  onClickRetrieveCode() {
    this.auth.updateLog('Signup detail: Clicked forgot code').then(async () => {
      this.resetValidation();
      this.currentStep = SignupDetailSteps.RETRIEVE_EMAIL;
    });
  }

  onClickContactUs() {
    this.auth.updateLog('Signup detail: Clicked contact us').then(() => {
      this.resetValidation();
      // if we have an invite code, we want to include it
      // if we have email, we want to include it
      // if we don't know the study, what do we do?
      // we could have a generic zendesk area for this, but need to forward canadian requests to canadian servers
      this.presentModalContact();
    });
  }

  /***********************************************************************************************************************************************************************
   * Log
   ***********************************************************************************************************************************************************************/

  postLog(detail) {
    // don't send the post if no invite code
    if (this.inviteCode == null) {
      return Promise.resolve();
    }

    this.logger.info('Signup', MSRemoteLoggerCategory.SETUP_SIGNUP_DETAIL_PAGE, {
      description: detail,
      code: this.inviteCode
    });
  }

  /***********************************************************************************************************************************************************************
   * Navigation
   ***********************************************************************************************************************************************************************/

  async back() {
    if (this.currentStep === this.firstStep) {
      this.dismiss();
    } else {
      if (this.currentStep === SignupDetailSteps.PARTICIPATION_CODE) {
        this.currentStep = SignupDetailSteps.ENTER_EMAIL;
      } else if (this.currentStep === SignupDetailSteps.RETRIEVE_EMAIL) {
        this.currentStep = SignupDetailSteps.PARTICIPATION_CODE;
      } else if (this.currentStep === SignupDetailSteps.ENTER_RETRIEVED_CODE) {
        this.currentStep = SignupDetailSteps.RETRIEVE_EMAIL;
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  async handleKeyboardEvent(event: KeyboardEvent) {
    const overlayController: OverlayBaseController<any, any> = this.isModal === true ? this.modalCtrl : this.popoverCtrl;
    const isTop = await isTopOverlay(overlayController, SignupDetailPage);
    if (isTop) {
      this.dismiss();
    }
  }

  dismiss() {
    const overlayController = this.isModal === true ? this.modalCtrl : this.popoverCtrl;
    overlayController.dismiss();
  }

  /***********************************************************************************************************************************************************************
   * Init
   ***********************************************************************************************************************************************************************/

  async setAppData() {
    const appInfo = await this.appAdapter.getAppInfo().toPromise();
    const deviceInfo = await this.deviceAdapter.getDeviceInfo().toPromise();

    this.appVer = appInfo.version;
    this.appId = appInfo.id;
    this.device = deviceInfo.platform;
  }

  ionViewWillEnter() {
    this.setAppData()
      .then(() => this.auth.getSignupEmail())
      .then(async (res: string) => {
        this.signupEmail = res;
        this.translationData = { email: this.email, inviteCode: this.inviteCode };

        // new invite code - we have access code and not already activated
        if (this.inviteCode != null && this.alreadyActivated === false) {
          this.currentStep = SignupDetailSteps.ENTER_EMAIL;
          this.firstStep = SignupDetailSteps.ENTER_EMAIL;
          // used invite code - we have access code and already activated
        } else if (this.inviteCode != null && this.alreadyActivated === true) {
          this.currentStep = SignupDetailSteps.PARTICIPATION_CODE;
          this.firstStep = SignupDetailSteps.PARTICIPATION_CODE;
          // retrieve email - no access code
        } else {
          this.currentStep = SignupDetailSteps.RETRIEVE_EMAIL;
          this.firstStep = SignupDetailSteps.RETRIEVE_EMAIL;
        }
      });
  }

  ngOnInit() {}
}
